import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Contractor, Project } from '../../../types';
import api from '../../../services/api';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface ProjectAssignment {
  id: string;
  contractorId: string;
  projectId: string;
  contractor: {
    id: string;
    name: string;
  };
  project: {
    id: string;
    name: string;
    isActive?: boolean;
    createdAt?: string;
    updatedAt?: string;
  };
}

const ProjectAssignmentTab: React.FC = () => {
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [assignments, setAssignments] = useState<ProjectAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fetchingDropdowns, setFetchingDropdowns] = useState(false);
  const [formData, setFormData] = useState({
    contractorId: '',
    projectId: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
    monthlyBudgetedHours: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching initial data...');
      
      const [contractorsRes, projectsRes, assignmentsRes] = await Promise.all([
        api.get('/contractors?isActive=true'),
        api.get('/projects?isActive=true'),
        api.get('/project-assignments'),
      ]);
      
      const contractorsData = contractorsRes.data || [];
      const projectsData = projectsRes.data || [];
      const assignmentsData = assignmentsRes.data || [];
      
      console.log('✅ Initial data fetched:');
      console.log(`  - Contractors: ${contractorsData.length}`);
      console.log(`  - Projects: ${projectsData.length}`);
      console.log(`  - Assignments: ${assignmentsData.length}`);
      
      // Set initial data (will be refreshed when dialog opens)
      setContractors(contractorsData);
      setProjects(projectsData);
      setAssignments(assignmentsData);
    } catch (error: any) {
      console.error('❌ Error fetching initial data:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      alert('Failed to load data: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      setFetchingDropdowns(true);
      console.log('🔄 Fetching contractors and projects for dropdowns...');
      
      const [contractorsRes, projectsRes] = await Promise.all([
        api.get('/contractors?isActive=true'),
        api.get('/projects?isActive=true'),
      ]);
      
      const contractorsData = contractorsRes.data || [];
      const projectsData = projectsRes.data || [];
      
      console.log('📊 Raw contractors response:', contractorsData.length);
      console.log('📊 Raw projects response:', projectsData.length);
      
      // Ensure we have valid data
      if (!Array.isArray(contractorsData)) {
        console.error('❌ Contractors data is not an array:', contractorsData);
        setContractors([]);
      } else {
        setContractors(contractorsData);
        console.log('✅ Contractors set:', contractorsData.length);
        contractorsData.forEach((c: Contractor) => {
          console.log(`  - ${c.name} (${c.id})`);
        });
      }
      
      if (!Array.isArray(projectsData)) {
        console.error('❌ Projects data is not an array:', projectsData);
        setProjects([]);
      } else {
        setProjects(projectsData);
        console.log('✅ Projects set:', projectsData.length);
        projectsData.forEach((p: Project) => {
          console.log(`  - ${p.name} (${p.id})`);
        });
      }
      
      if (contractorsData.length === 0) {
        console.warn('⚠️ No active contractors found. Check if contractors exist and are active.');
      }
      if (projectsData.length === 0) {
        console.warn('⚠️ No active projects found. Check if projects exist and are active.');
      }
    } catch (error: any) {
      console.error('❌ Error fetching dropdowns:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      alert('Failed to load contractors/projects: ' + (error.response?.data?.message || error.message));
      // Set empty arrays on error to prevent UI issues
      setContractors([]);
      setProjects([]);
    } finally {
      setFetchingDropdowns(false);
    }
  };

  const handleOpen = async () => {
    setFormData({
      contractorId: '',
      projectId: '',
      startDate: null,
      endDate: null,
      monthlyBudgetedHours: '',
    });
    // Refresh dropdowns when opening dialog
    await fetchDropdowns();
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleSubmit = async () => {
    try {
      await api.post('/project-assignments', {
        contractorId: formData.contractorId,
        projectId: formData.projectId,
        monthlyBudgetedHours: formData.monthlyBudgetedHours ? parseFloat(formData.monthlyBudgetedHours) : null,
      });
      fetchData();
      handleClose();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to assign contractor');
    }
  };

  const handleRemove = async (contractorId: string, projectId: string) => {
    if (!window.confirm('Are you sure you want to remove this assignment?')) {
      return;
    }

    try {
      await api.delete(`/project-assignments/${contractorId}/${projectId}`);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to remove assignment');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const groupedByProject = assignments.reduce((acc, assignment) => {
    if (!acc[assignment.projectId]) {
      acc[assignment.projectId] = {
        project: {
          id: assignment.project.id,
          name: assignment.project.name,
          isActive: assignment.project.isActive ?? true,
          createdAt: assignment.project.createdAt || new Date().toISOString(),
          updatedAt: assignment.project.updatedAt || new Date().toISOString(),
        } as Project,
        contractors: [] as Contractor[],
      };
    }
    // Find full contractor data from contractors list
    const fullContractor = contractors.find(c => c.id === assignment.contractor.id);
    if (fullContractor) {
      acc[assignment.projectId].contractors.push(fullContractor);
    } else {
      // Fallback: create minimal contractor object
      acc[assignment.projectId].contractors.push({
        id: assignment.contractor.id,
        name: assignment.contractor.name,
        userId: '',
        hourlyRate: 0,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as Contractor);
    }
    return acc;
  }, {} as Record<string, { project: Project; contractors: Contractor[] }>);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Project Assignment & Tracking</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
          Assign Contractor to Project
        </Button>
      </Box>

      {Object.keys(groupedByProject).length === 0 ? (
        <Alert severity="info">No project assignments found. Click "Assign Contractor to Project" to get started.</Alert>
      ) : (
        <Grid container spacing={2}>
          {Object.values(groupedByProject).map((group) => (
            <Grid item xs={12} md={6} key={group.project.id}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {group.project.name}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Contractor</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.contractors.map((contractor) => {
                        return (
                          <TableRow key={contractor.id}>
                            <TableCell>{contractor.name}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Remove Assignment">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemove(contractor.id, group.project.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Contractor to Project</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Contractor *</InputLabel>
              <Select
                value={formData.contractorId}
                label="Contractor *"
                onChange={(e) => {
                  console.log('Contractor selected:', e.target.value);
                  setFormData({ ...formData, contractorId: e.target.value });
                }}
                disabled={fetchingDropdowns}
              >
                {fetchingDropdowns ? (
                  <MenuItem disabled>Loading contractors...</MenuItem>
                ) : contractors.length === 0 ? (
                  <MenuItem disabled>No active contractors available</MenuItem>
                ) : (
                  contractors.map((contractor) => (
                    <MenuItem key={contractor.id} value={contractor.id}>
                      {contractor.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {!fetchingDropdowns && contractors.length === 0 && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  No active contractors found. Please create a contractor first.
                </Typography>
              )}
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Project *</InputLabel>
              <Select
                value={formData.projectId}
                label="Project *"
                onChange={(e) => {
                  console.log('Project selected:', e.target.value);
                  setFormData({ ...formData, projectId: e.target.value });
                }}
                disabled={fetchingDropdowns}
              >
                {fetchingDropdowns ? (
                  <MenuItem disabled>Loading projects...</MenuItem>
                ) : projects.length === 0 ? (
                  <MenuItem disabled>No active projects available</MenuItem>
                ) : (
                  projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {!fetchingDropdowns && projects.length === 0 && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  No active projects found. Please create a project first.
                </Typography>
              )}
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date (optional)"
                value={formData.startDate}
                onChange={(date) => setFormData({ ...formData, startDate: date })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date (optional)"
                value={formData.endDate}
                onChange={(date) => setFormData({ ...formData, endDate: date })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
            <TextField
              label="Monthly Budgeted Hours (optional)"
              type="number"
              value={formData.monthlyBudgetedHours}
              onChange={(e) => setFormData({ ...formData, monthlyBudgetedHours: e.target.value })}
              fullWidth
              inputProps={{ step: '0.01', min: '0' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.contractorId || !formData.projectId}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectAssignmentTab;

